import { isAxiosError } from "axios";
import equal from "deep-equal";

import { backendOld } from "~/api";
import { notify } from "~/components/notifications";
import { getEmojiWithSkinTone, normalizeEmoji, removeSkinTone } from "~/constants/iconAndEmoji";
import { DART_AI_NAME } from "~/constants/userKinds";
import { isDefined } from "~/shared/common";
import { IconKind, NotificationType } from "~/shared/enums";
import type { Task, TaskField, TaskFieldValue, TaskUpdate } from "~/shared/types";
import { useDataStore, usePageStore, useUserStore } from "~/stores";

const FIELD_TO_NOTIFY_MESSAGE_MAP = new Map<TaskField, string>([
  ["assigneeDuids", "assignee"],
  ["tagDuids", "tag"],
  ["dueAt", "due date"],
]);

export const generateFieldRecommendation = async (
  tasks: Task[],
  field: TaskField,
  defaultValue: TaskFieldValue
): Promise<[TaskUpdate[], string[]]> => {
  let responses;
  try {
    // TODO convert this endpoint to bulk
    responses = await Promise.all(tasks.map(({ duid }) => backendOld.recommendations.getTaskProperties(duid, [field])));
  } catch (error) {
    if (isAxiosError<{ errors: string[] }>(error)) {
      notify({ message: error.response?.data.errors[0], type: NotificationType.ERROR });
    }
    return [[], []];
  }

  const items = responses.map((res) => res.data.items[0]).filter(isDefined);
  const updates = items
    .filter((item) => item && !equal(item.value, defaultValue), { strict: true })
    .map(({ duid, value, field: taskRecField }) => ({
      duid,
      [taskRecField]: value,
    }));
  const recommendationDuids = items.map(({ recommendationDuid }) => recommendationDuid);

  if (updates.length === 0) {
    notify({
      message: `${DART_AI_NAME} has no ${FIELD_TO_NOTIFY_MESSAGE_MAP.get(field) ?? field} recommendation${
        tasks.length === 1 ? " for this task" : "s for these tasks"
      }. Add more details and try again.`,
    });
  }
  return [updates, recommendationDuids];
};

export const getRecommendationDefaultValuesForProperties = () => {
  const dataStore = useDataStore();
  return new Map<string, [TaskField, TaskFieldValue]>([
    [dataStore.defaultAssigneesProperty.duid, ["assigneeDuids", []]],
    [dataStore.defaultTagsProperty.duid, ["tagDuids", []]],
    [dataStore.defaultPriorityProperty.duid, ["priority", null]],
    [dataStore.defaultSizeProperty.duid, ["size", null]],
    [dataStore.defaultDatesProperty.duid, ["dueAt", null]],
  ]);
};

export const getEmojiRecommendation = async (
  duid: string,
  newTitle?: string | null
): Promise<{ iconKind: IconKind.EMOJI; iconNameOrEmoji: string } | undefined> => {
  const pageStore = usePageStore();
  const userStore = useUserStore();

  if (!pageStore.isOnline) {
    return undefined;
  }

  const res = await backendOld.recommendations.getPageEmoji(duid, newTitle);

  const emoji = normalizeEmoji(removeSkinTone(res.data.item.value));
  userStore.addToRecentEmojiStack(emoji);
  return { iconKind: IconKind.EMOJI, iconNameOrEmoji: getEmojiWithSkinTone(emoji, userStore.skinTone) };
};
